import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import DOMPurify from 'dompurify'
import { supabase } from '../lib/supabase'
import { dbFetch } from '../lib/dbFetch'
import { notifyShareUpdate } from '../lib/notifyShareUpdate'
import useAuthStore from './authStore'

// Sanitize user input before saving to DB
const sanitize = (str) => typeof str === 'string' ? DOMPurify.sanitize(str, { ALLOWED_TAGS: [] }) : str

// `dbFetch` è ora estratto in `src/lib/dbFetch.js` (per riuso da altri store).

// ── Mappers: snake_case DB → camelCase JS ────────────────────────────────────

const mapTask = (t) => ({
  id:                    t.id,
  title:                 t.title,
  category:              t.category,
  dueDate:               t.due_date,
  dueTime:               t.due_time   ?? '',
  priority:              t.priority,
  recurring:             t.recurring,
  recurringInterval:     t.recurring_interval,
  recurringUnit:         t.recurring_unit,
  notes:                 t.notes,
  completed:             t.completed,
  completedAt:           t.completed_at ?? null,
  reminder:              t.reminder         ?? false,
  reminderMinutes:       t.reminder_minutes ?? 15,
  durationMinutes:       t.duration_minutes ?? null,
  eisenhowerQuadrant:    t.eisenhower_quadrant ?? null,
  eisenhowerRationale:   t.eisenhower_rationale ?? null,
  eisenhowerClassifiedAt: t.eisenhower_classified_at ?? null,
  practiceId:            t.practice_id ?? null,
  sharedOriginId:        t.shared_origin_id ?? null,
  createdAt:             t.created_at,
  updatedAt:             t.updated_at ?? null,
})

// Share "outbound" mapper: stato discreto derivato dai timestamp
// (così non serve cron per "expired").
const mapOutboundShare = (s) => {
  let derivedStatus = s.status
  const isFinal = s.status === 'declined' || s.status === 'deleted' || s.status === 'completed'
  if (!isFinal && s.expires_at && new Date(s.expires_at) < new Date() && s.status === 'pending') {
    derivedStatus = 'expired'
  }
  return {
    id:                   s.id,
    senderTaskId:         s.sender_task_id,
    recipientEmail:       s.recipient_email,
    recipientId:          s.recipient_id,
    recipientTaskId:      s.recipient_task_id,
    status:               derivedStatus,
    acceptedAt:           s.accepted_at,
    recipientCompletedAt: s.recipient_completed_at,
    recipientDeletedAt:   s.recipient_deleted_at,
    expiresAt:            s.expires_at,
    createdAt:            s.created_at,
  }
}

const mapScadenza = (s) => ({
  id:              s.id,
  title:           s.title,
  category:        s.category,
  dueDate:         s.due_date,
  recurringMonths: s.recurring_months,
  lastDone:        s.last_done,
  createdAt:       s.created_at,
})

const mapBlocco = (b) => ({
  id:                 b.id,
  type:               b.type,
  label:              b.label ?? '',
  startAt:            b.start_at,
  endAt:              b.end_at,
  recurringGroupId:   b.recurring_group_id ?? null,
  recurrence:         b.recurrence ?? 'once',
  createdAt:          b.created_at,
})

// camelCase JS → snake_case DB (sanitized)
const toDbTask = (data) => ({
  title:              sanitize(data.title),
  category:           data.category,
  due_date:           data.dueDate           || null,
  due_time:           data.dueTime           || null,
  priority:           data.priority,
  recurring:          data.recurring         ?? false,
  recurring_interval: data.recurringInterval ?? null,
  recurring_unit:     data.recurringUnit     ?? null,
  notes:              sanitize(data.notes)    ?? '',
  completed:          data.completed         ?? false,
  reminder:           data.reminder          ?? false,
  reminder_minutes:   data.reminderMinutes   ?? 15,
  duration_minutes:   data.durationMinutes   ?? null,
  practice_id:        data.practiceId        ?? null,
})

const toDbScadenza = (data) => ({
  title:            sanitize(data.title),
  category:         data.category,
  due_date:         data.dueDate         || null,
  recurring_months: data.recurringMonths ?? 12,
})

const toDbBlocco = (data) => ({
  type:                 data.type,
  label:                sanitize(data.label ?? '') || '',
  start_at:             data.startAt,              // ISO string o Date.toISOString()
  end_at:               data.endAt,
  recurring_group_id:   data.recurringGroupId ?? null,
  recurrence:           data.recurrence       ?? 'once',
})

const byStartAt = (a, b) => new Date(a.startAt) - new Date(b.startAt)

const byDueDate = (a, b) => {
  if (!a.dueDate && !b.dueDate) return 0
  if (!a.dueDate) return 1
  if (!b.dueDate) return -1
  return a.dueDate.localeCompare(b.dueDate)
}

// ── Store ────────────────────────────────────────────────────────────────────

const useTaskStore = create(
  persist(
    (set, get) => ({
  // UI state
  filter:              'all',
  isQuickAddOpen:      false,
  quickAddMode:        'manual',
  quickAddInitialData: null,
  isPremiumModalOpen:  false,
  premiumModalHighlight: null,  // null | 'plus' | 'premium'
  isAgentOpen:         false,
  agentInitialMessage: null,

  // Data (null = non ancora caricato)
  tasks:    null,
  scadenze: null,
  blocchi:  null,

  // Mappa { senderTaskId: [share, ...] } — usata dal sender per mostrare
  // gli stati discreti delle copie dei recipient. Non viene persistita
  // (è uno snapshot derivato, ricaricato a ogni initRealtime).
  outboundShares: {},

  // Realtime channel refs (interni)
  _tasksChannel:        null,
  _scadenzeChannel:     null,
  _blocchiChannel:      null,
  _sharesChannel:       null,
  _inboundSharesChannel: null,

  // Stato sincronizzazione
  syncing: false,

  // ── UI actions ─────────────────────────────────────────────────────────────
  setFilter: (filter) => set({ filter }),

  openQuickAdd: (mode = 'manual', initialData = null) =>
    set({ isQuickAddOpen: true, quickAddMode: mode, quickAddInitialData: initialData }),

  closeQuickAdd: () =>
    set({ isQuickAddOpen: false, quickAddInitialData: null }),

  openPremiumModal:  (highlight = null) => set({ isPremiumModalOpen: true, premiumModalHighlight: highlight }),
  closePremiumModal: () => set({ isPremiumModalOpen: false, premiumModalHighlight: null }),

  openAgent:  (initialMessage = null) => set({ isAgentOpen: true, agentInitialMessage: initialMessage }),
  closeAgent: () => set({ isAgentOpen: false, agentInitialMessage: null }),

  // ── Fetch iniziale ─────────────────────────────────────────────────────────
  // Fetch difensiva con timeout: se supabase.from() si blocca internamente
  // (navigator.locks stuck, retry loop, ecc.), dopo 5s rilasciamo syncing
  // e non freezziamo la UI. Realtime aggiornerà comunque lo store al prossimo evento.
  fetchTasks: async () => {
    const userId = useAuthStore.getState().user?.id
    if (!userId) return
    set({ syncing: true })
    try {
      const result = await Promise.race([
        supabase.from('tasks').select('*').eq('user_id', userId).order('due_date', { ascending: true, nullsFirst: false }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('fetchTasks timeout')), 5000)),
      ])
      set({ tasks: (result?.data ?? []).map(mapTask) })
    } catch (err) {
      console.warn('[taskStore.fetchTasks]', err.message)
    } finally {
      set({ syncing: false })
    }
  },

  fetchScadenze: async () => {
    const userId = useAuthStore.getState().user?.id
    if (!userId) return
    set({ syncing: true })
    try {
      const result = await Promise.race([
        supabase.from('scadenze').select('*').eq('user_id', userId).order('due_date', { ascending: true, nullsFirst: false }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('fetchScadenze timeout')), 5000)),
      ])
      set({ scadenze: (result?.data ?? []).map(mapScadenza) })
    } catch (err) {
      console.warn('[taskStore.fetchScadenze]', err.message)
    } finally {
      set({ syncing: false })
    }
  },

  fetchBlocchi: async () => {
    const userId = useAuthStore.getState().user?.id
    if (!userId) return
    set({ syncing: true })
    try {
      const result = await Promise.race([
        supabase.from('blocchi').select('*').eq('user_id', userId).order('start_at', { ascending: true }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('fetchBlocchi timeout')), 5000)),
      ])
      set({ blocchi: (result?.data ?? []).map(mapBlocco) })
    } catch (err) {
      console.warn('[taskStore.fetchBlocchi]', err.message)
    } finally {
      set({ syncing: false })
    }
  },

  // Carica le condivisioni "in uscita": righe shared_tasks dove sono il sender,
  // raggruppate per sender_task_id (legate al task originale). Le righe orfane
  // (sender_task_id = NULL, perché il sender ha cancellato il proprio task)
  // sono escluse dal raggruppamento — restano in DB per audit ma non hanno UI.
  fetchOutboundShares: async () => {
    const userId = useAuthStore.getState().user?.id
    if (!userId) return
    try {
      const result = await Promise.race([
        supabase.from('shared_tasks').select('*').eq('sender_id', userId).not('sender_task_id', 'is', null),
        new Promise((_, reject) => setTimeout(() => reject(new Error('fetchOutboundShares timeout')), 5000)),
      ])
      const grouped = {}
      for (const row of (result?.data ?? [])) {
        const mapped = mapOutboundShare(row)
        if (!grouped[mapped.senderTaskId]) grouped[mapped.senderTaskId] = []
        grouped[mapped.senderTaskId].push(mapped)
      }
      set({ outboundShares: grouped })
    } catch (err) {
      console.warn('[taskStore.fetchOutboundShares]', err.message)
    }
  },

  // ── Realtime (una sola subscription per tutta l'app) ───────────────────────
  initRealtime: () => {
    const userId = useAuthStore.getState().user?.id
    if (!userId) return

    // Fetch iniziale
    get().fetchTasks()
    get().fetchScadenze()
    get().fetchBlocchi()
    get().fetchOutboundShares()

    const tasksChannel = supabase
      .channel(`store-tasks:${userId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'tasks', filter: `user_id=eq.${userId}` },
        ({ new: row }) => set(s => {
          const existing = s.tasks ?? []
          // Dedup: se abbiamo già un optimistic o Realtime duplicato, sostituisci.
          const idx = existing.findIndex(t => t.id === row.id)
          const next = idx >= 0
            ? existing.map(t => t.id === row.id ? mapTask(row) : t)
            : [...existing, mapTask(row)]
          return { tasks: next.sort(byDueDate) }
        })
      )
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'tasks', filter: `user_id=eq.${userId}` },
        ({ new: row }) => set(s => ({
          tasks: (s.tasks ?? []).map(t => t.id === row.id ? mapTask(row) : t)
        }))
      )
      .on('postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'tasks', filter: `user_id=eq.${userId}` },
        ({ old: row }) => set(s => ({
          tasks: (s.tasks ?? []).filter(t => t.id !== row.id)
        }))
      )
      .subscribe()

    const scadenzeChannel = supabase
      .channel(`store-scadenze:${userId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'scadenze', filter: `user_id=eq.${userId}` },
        ({ new: row }) => set(s => {
          const existing = s.scadenze ?? []
          const idx = existing.findIndex(x => x.id === row.id)
          const next = idx >= 0
            ? existing.map(x => x.id === row.id ? mapScadenza(row) : x)
            : [...existing, mapScadenza(row)]
          return { scadenze: next.sort(byDueDate) }
        })
      )
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'scadenze', filter: `user_id=eq.${userId}` },
        ({ new: row }) => set(s => ({
          scadenze: (s.scadenze ?? []).map(x => x.id === row.id ? mapScadenza(row) : x)
        }))
      )
      .on('postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'scadenze', filter: `user_id=eq.${userId}` },
        ({ old: row }) => set(s => ({
          scadenze: (s.scadenze ?? []).filter(x => x.id !== row.id)
        }))
      )
      .subscribe()

    const blocchiChannel = supabase
      .channel(`store-blocchi:${userId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'blocchi', filter: `user_id=eq.${userId}` },
        ({ new: row }) => set(s => {
          const existing = s.blocchi ?? []
          const idx = existing.findIndex(b => b.id === row.id)
          const next = idx >= 0
            ? existing.map(b => b.id === row.id ? mapBlocco(row) : b)
            : [...existing, mapBlocco(row)]
          return { blocchi: next.sort(byStartAt) }
        })
      )
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'blocchi', filter: `user_id=eq.${userId}` },
        ({ new: row }) => set(s => ({
          blocchi: (s.blocchi ?? []).map(b => b.id === row.id ? mapBlocco(row) : b)
        }))
      )
      .on('postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'blocchi', filter: `user_id=eq.${userId}` },
        ({ old: row }) => set(s => ({
          blocchi: (s.blocchi ?? []).filter(b => b.id !== row.id)
        }))
      )
      .subscribe()

    // ── shared_tasks (lato sender): aggiorna `outboundShares` quando il
    //    recipient cambia stato (accept/complete/uncomplete/delete via trigger
    //    su `tasks`) o quando un nuovo share viene creato.
    //    Filtro server-side per sender_id evita di ricevere righe di altri.
    const sharesChannel = supabase
      .channel(`store-shares:${userId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'shared_tasks', filter: `sender_id=eq.${userId}` },
        ({ new: row }) => set(s => {
          if (!row.sender_task_id) return s
          const mapped = mapOutboundShare(row)
          const list = s.outboundShares[row.sender_task_id] ?? []
          const idx = list.findIndex(x => x.id === mapped.id)
          const next = idx >= 0
            ? list.map(x => x.id === mapped.id ? mapped : x)
            : [...list, mapped]
          return { outboundShares: { ...s.outboundShares, [row.sender_task_id]: next } }
        })
      )
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'shared_tasks', filter: `sender_id=eq.${userId}` },
        ({ new: row }) => set(s => {
          if (!row.sender_task_id) return s
          const mapped = mapOutboundShare(row)
          const list = s.outboundShares[row.sender_task_id] ?? []
          const next = list.findIndex(x => x.id === mapped.id) >= 0
            ? list.map(x => x.id === mapped.id ? mapped : x)
            : [...list, mapped]
          return { outboundShares: { ...s.outboundShares, [row.sender_task_id]: next } }
        })
      )
      .on('postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'shared_tasks', filter: `sender_id=eq.${userId}` },
        ({ old: row }) => set(s => {
          const key = row.sender_task_id
          if (!key || !s.outboundShares[key]) return s
          const next = s.outboundShares[key].filter(x => x.id !== row.id)
          return { outboundShares: { ...s.outboundShares, [key]: next } }
        })
      )
      .subscribe()

    // ── shared_tasks IN INGRESSO (recipient = me): aggiunge banner di
    //    accettazione in Real-Time. Prima di questo channel, gli share in
    //    arrivo erano scoperti solo via `_fetchProfile` al boot o
    //    `checkPendingShares` al visibilitychange — race contro la notifica
    //    push (l'utente apriva l'app prima che la SELECT vedesse la riga).
    //    Con questa subscription la INSERT propaga immediatamente in
    //    authStore.pendingShares.
    const inboundSharesChannel = supabase
      .channel(`store-inbound-shares:${userId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'shared_tasks', filter: `recipient_id=eq.${userId}` },
        ({ new: row }) => {
          if (row.status !== 'pending') return
          if (row.expires_at && new Date(row.expires_at) < new Date()) return
          useAuthStore.setState(s => {
            if (s.pendingShares.some(x => x.id === row.id)) return s
            return { pendingShares: [...s.pendingShares, row] }
          })
        }
      )
      .subscribe()

    set({
      _tasksChannel: tasksChannel,
      _scadenzeChannel: scadenzeChannel,
      _blocchiChannel: blocchiChannel,
      _sharesChannel:  sharesChannel,
      _inboundSharesChannel: inboundSharesChannel,
    })
  },

  destroyRealtime: () => {
    const { _tasksChannel, _scadenzeChannel, _blocchiChannel, _sharesChannel, _inboundSharesChannel } = get()
    if (_tasksChannel)         supabase.removeChannel(_tasksChannel)
    if (_scadenzeChannel)      supabase.removeChannel(_scadenzeChannel)
    if (_blocchiChannel)       supabase.removeChannel(_blocchiChannel)
    if (_sharesChannel)        supabase.removeChannel(_sharesChannel)
    if (_inboundSharesChannel) supabase.removeChannel(_inboundSharesChannel)
    set({
      tasks: null, scadenze: null, blocchi: null, outboundShares: {},
      _tasksChannel: null, _scadenzeChannel: null, _blocchiChannel: null,
      _sharesChannel: null, _inboundSharesChannel: null,
    })
  },

  // ── Tasks CRUD ─────────────────────────────────────────────────────────────
  addTask: async (taskData) => {
    const userId = useAuthStore.getState().user?.id
    if (!userId) throw new Error('Not authenticated')

    // Client-side free plan limit (server enforces it too via trigger)
    const user = useAuthStore.getState().user
    if (user?.plan !== 'pro' && user?.plan !== 'premium') {
      const currentTasks = get().tasks
      if (currentTasks && currentTasks.length >= 20) {
        get().openPremiumModal()
        throw new Error('task_limit')
      }
    }

    // Optimistic: generiamo l'UUID lato client, inseriamo subito nello store,
    // poi fire INSERT al DB. Se fallisce, rollback. In caso di successo il
    // Realtime INSERT arriverà con stesso id e verrà deduplicato (vedi handler).
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`
    const now = new Date().toISOString()
    const sharedOriginId = taskData.sharedOriginId ?? null
    const optimistic = mapTask({
      id,
      ...toDbTask(taskData),
      shared_origin_id: sharedOriginId,
      user_id: userId,
      created_at: now,
      completed: taskData.completed ?? false,
    })
    set(s => ({ tasks: [...(s.tasks ?? []), optimistic].sort(byDueDate) }))

    try {
      await dbFetch('tasks', 'POST', {
        id,
        ...toDbTask(taskData),
        ...(sharedOriginId ? { shared_origin_id: sharedOriginId } : {}),
        user_id: userId,
      })
      // Backfill: lega la riga shared_tasks alla copia importata, così il
      // sender può navigare verso la copia del recipient e il trigger ha
      // un puntatore stabile. Fire-and-forget: se fallisce il task esiste
      // comunque, il legame ledger→copia è solo "comodità".
      if (sharedOriginId) {
        dbFetch(`shared_tasks?id=eq.${sharedOriginId}`, 'PATCH', { recipient_task_id: id })
          .catch(err => console.warn('[taskStore.addTask] recipient_task_id backfill failed', err.message))
      }
      return id
    } catch (err) {
      // Rollback ottimistico
      set(s => ({ tasks: (s.tasks ?? []).filter(t => t.id !== id) }))
      // Server-side task limit (trigger enforce_task_limit)
      if (err.message?.includes('limite') || err.message?.includes('limit')) {
        get().openPremiumModal()
        throw new Error('task_limit')
      }
      throw err
    }
  },

  toggleTask: async (id) => {
    const task = get().tasks?.find(t => t.id === id)
    if (!task) return
    const newCompleted = !task.completed
    // Optimistic: update subito, realtime riconcilia
    set(s => ({
      tasks: (s.tasks ?? []).map(t => t.id === id ? { ...t, completed: newCompleted } : t),
    }))
    try {
      await dbFetch(`tasks?id=eq.${id}`, 'PATCH', { completed: newCompleted })
      // Se è una copia ricevuta E sto completando (non toggling off), avvisa
      // il sender con un push. Sul "uncomplete" non mandiamo niente per
      // evitare rumore. Errori loggati ma non bloccano: la chip del sender
      // si aggiorna comunque via trigger DB + Realtime.
      if (task.sharedOriginId && newCompleted) {
        notifyShareUpdate(task.sharedOriginId, 'completed')
      }
    } catch (err) {
      // Rollback
      set(s => ({
        tasks: (s.tasks ?? []).map(t => t.id === id ? { ...t, completed: task.completed } : t),
      }))
      throw err
    }
  },

  deleteTask: async (id) => {
    const task = get().tasks?.find(t => t.id === id)
    await dbFetch(`tasks?id=eq.${id}`, 'DELETE')
    set(s => ({ tasks: (s.tasks ?? []).filter(t => t.id !== id) }))
    if (task?.sharedOriginId) {
      notifyShareUpdate(task.sharedOriginId, 'deleted')
    }
  },

  updateTask: async (id, data) => {
    const prev = get().tasks?.find(t => t.id === id)
    if (!prev) return
    // Optimistic
    set(s => ({
      tasks: (s.tasks ?? []).map(t => t.id === id ? { ...t, ...data } : t),
    }))
    try {
      await dbFetch(`tasks?id=eq.${id}`, 'PATCH', toDbTask(data))
    } catch (err) {
      // Rollback
      set(s => ({
        tasks: (s.tasks ?? []).map(t => t.id === id ? prev : t),
      }))
      throw err
    }
  },

  // Posponi un'attività spostando il prossimo "ricordo" all'opzione scelta.
  // option indica QUANDO vuole essere ricordato l'utente (15m/30m/1h/3h/19:00/9:00 domani).
  // La nuova scadenza viene calcolata come (target reminder time + reminder_minutes)
  // così il reminder ricalcolato scatterà esattamente all'ora richiesta dall'utente.
  // reminder_sent_at viene azzerato per riabilitare l'invio.
  // option: '15m' | '30m' | '1h' | '3h' | 'tonight' | 'tomorrow'
  snoozeTask: async (id, option) => {
    const prev = get().tasks?.find(t => t.id === id)
    if (!prev) return

    const pad = (n) => n.toString().padStart(2, '0')
    const now = new Date()
    const targetReminder = new Date(now)

    switch (option) {
      case '15m':      targetReminder.setMinutes(now.getMinutes() + 15); break
      case '30m':      targetReminder.setMinutes(now.getMinutes() + 30); break
      case '1h':       targetReminder.setHours(now.getHours() + 1);     break
      case '3h':       targetReminder.setHours(now.getHours() + 3);     break
      case 'tonight':
        targetReminder.setHours(19, 0, 0, 0)
        if (targetReminder.getTime() <= now.getTime()) targetReminder.setDate(targetReminder.getDate() + 1)
        break
      case 'tomorrow':
        targetReminder.setDate(targetReminder.getDate() + 1)
        targetReminder.setHours(9, 0, 0, 0)
        break
      default: return
    }

    // Sposta la scadenza in modo che il reminder scatti esattamente a targetReminder.
    const reminderMins = prev.reminder ? (prev.reminderMinutes ?? 15) : 0
    const newDue = new Date(targetReminder.getTime() + reminderMins * 60 * 1000)

    const dueDate = `${newDue.getFullYear()}-${pad(newDue.getMonth() + 1)}-${pad(newDue.getDate())}`
    const dueTime = `${pad(newDue.getHours())}:${pad(newDue.getMinutes())}`

    set(s => ({
      tasks: (s.tasks ?? []).map(t => t.id === id
        ? { ...t, dueDate, dueTime, reminderSentAt: null }
        : t),
    }))
    try {
      await dbFetch(`tasks?id=eq.${id}`, 'PATCH', {
        due_date:         dueDate,
        due_time:         dueTime,
        reminder_sent_at: null,
      })
    } catch (err) {
      set(s => ({
        tasks: (s.tasks ?? []).map(t => t.id === id ? prev : t),
      }))
      throw err
    }
  },

  // Cambio manuale del quadrante Eisenhower (es. drag&drop). Update mirato
  // SENZA passare da toDbTask (che filtrerebbe il campo) e SENZA triggerare
  // il reset automatico (il trigger `reset_eisenhower_on_change` scatta solo
  // su title/date/priority/category, non su questo campo).
  setEisenhowerQuadrant: async (id, quadrant) => {
    const prev = get().tasks?.find(t => t.id === id)
    if (!prev) return
    const rationale = 'Spostato manualmente'
    const classifiedAt = new Date().toISOString()
    // Optimistic
    set(s => ({
      tasks: (s.tasks ?? []).map(t => t.id === id ? {
        ...t,
        eisenhowerQuadrant: quadrant,
        eisenhowerRationale: rationale,
        eisenhowerClassifiedAt: classifiedAt,
      } : t),
    }))
    try {
      await dbFetch(`tasks?id=eq.${id}`, 'PATCH', {
        eisenhower_quadrant: quadrant,
        eisenhower_rationale: rationale,
        eisenhower_classified_at: classifiedAt,
      })
    } catch (err) {
      // Rollback
      set(s => ({
        tasks: (s.tasks ?? []).map(t => t.id === id ? prev : t),
      }))
      throw err
    }
  },

  // ── Scadenze CRUD ──────────────────────────────────────────────────────────
  addScadenza: async (data) => {
    const userId = useAuthStore.getState().user?.id
    if (!userId) throw new Error('Not authenticated')
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`
    const optimistic = mapScadenza({
      id,
      ...toDbScadenza(data),
      user_id: userId,
      last_done: null,
      created_at: new Date().toISOString(),
    })
    set(s => ({ scadenze: [...(s.scadenze ?? []), optimistic].sort(byDueDate) }))
    try {
      await dbFetch('scadenze', 'POST', { id, ...toDbScadenza(data), user_id: userId, last_done: null })
    } catch (err) {
      set(s => ({ scadenze: (s.scadenze ?? []).filter(x => x.id !== id) }))
      throw err
    }
  },

  updateScadenza: async (id, data) => {
    await dbFetch(`scadenze?id=eq.${id}`, 'PATCH', {
      ...toDbScadenza(data),
      last_done: data.lastDone ?? null,
    })
  },

  deleteScadenza: async (id) => {
    await dbFetch(`scadenze?id=eq.${id}`, 'DELETE')
    set(s => ({ scadenze: (s.scadenze ?? []).filter(sc => sc.id !== id) }))
  },

  // ── Blocchi (indisponibilità) CRUD ────────────────────────────────────────
  // addBlocco: { type, label, startAt, endAt, recurrence, weeksAhead? }
  // Se recurrence === 'weekly', materializziamo `weeksAhead` (default 12)
  // istanze condividendo un recurring_group_id. Scelta di design: istanze
  // materializzate invece di regole rrule per gestire facilmente eccezioni
  // (modificare una settimana non influenza le altre).
  // Args:
  //   type, label, startAt, endAt
  //   recurrence: 'once' | 'weekly'
  //   weekdays:    array di indici [0..6] con 0=Lun..6=Dom (solo weekly).
  //                Se mancante, usa il weekday di startAt.
  //   terminationMode: 'after' | 'never' | 'until'
  //   terminationWeeks: numero settimane per 'after' (default 12)
  //   terminationUntil: ISO date per 'until'
  //   weeksAhead: legacy fallback se terminationMode mancante (default 12).
  //   hardCap: protezione runaway (default 26 settimane per 'never').
  addBlocco: async ({
    type, label, startAt, endAt,
    recurrence = 'once',
    weekdays,
    terminationMode,
    terminationWeeks,
    terminationUntil,
    weeksAhead = 12,
    hardCap = 26,
  }) => {
    const userId = useAuthStore.getState().user?.id
    if (!userId) throw new Error('Not authenticated')

    const isWeekly = recurrence === 'weekly'
    const groupId = isWeekly
      ? (crypto.randomUUID?.() ?? `grp-${Date.now()}-${Math.random().toString(36).slice(2)}`)
      : null

    const baseStart = new Date(startAt)
    const baseEnd   = new Date(endAt)
    const durationMs = baseEnd.getTime() - baseStart.getTime()

    // Numero di settimane da generare in base alla termination mode.
    let totalWeeks = 1
    if (isWeekly) {
      if (terminationMode === 'after') totalWeeks = Math.max(1, terminationWeeks ?? weeksAhead)
      else if (terminationMode === 'never') totalWeeks = hardCap
      else if (terminationMode === 'until' && terminationUntil) {
        const untilMs = new Date(terminationUntil).getTime()
        const weeks = Math.ceil((untilMs - baseStart.getTime()) / (7 * 24 * 60 * 60 * 1000))
        totalWeeks = Math.max(1, Math.min(hardCap, weeks))
      } else {
        totalWeeks = Math.max(1, weeksAhead)
      }
    }

    // weekdays: 0 = Lun..6 = Dom. Se non passato, usa il weekday di startAt.
    // JS getDay() = 0..6 con Dom=0 → conversione Monday-first.
    const jsToMon = (js) => (js + 6) % 7
    const weekdaysArr = (isWeekly && weekdays?.length)
      ? [...weekdays].sort((a, b) => a - b)
      : [ jsToMon(baseStart.getDay()) ]

    // Lunedì della settimana di baseStart (a mezzanotte locale).
    const weekStart = new Date(baseStart)
    weekStart.setDate(baseStart.getDate() - jsToMon(baseStart.getDay()))
    weekStart.setHours(0, 0, 0, 0)

    const rows = []
    const optimisticItems = []
    if (!isWeekly) {
      // Singola occorrenza
      const id = crypto.randomUUID?.() ?? `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`
      const row = {
        id, user_id: userId,
        ...toDbBlocco({ type, label, startAt: baseStart.toISOString(), endAt: baseEnd.toISOString(), recurringGroupId: null, recurrence: 'once' }),
      }
      rows.push(row)
      optimisticItems.push(mapBlocco(row))
    } else {
      // Multi-weekday × totalWeeks
      const untilDate = terminationMode === 'until' && terminationUntil
        ? new Date(terminationUntil) : null
      for (let w = 0; w < totalWeeks; w++) {
        for (const wd of weekdaysArr) {
          const s = new Date(weekStart)
          s.setDate(weekStart.getDate() + w * 7 + wd)
          s.setHours(baseStart.getHours(), baseStart.getMinutes(), 0, 0)
          // Skip se nel passato del baseStart (prima settimana)
          if (s.getTime() < baseStart.getTime()) continue
          // Se termination=until, stop al superamento della data
          if (untilDate && s.getTime() > untilDate.getTime()) continue
          const e = new Date(s.getTime() + durationMs)
          const id = crypto.randomUUID?.() ?? `tmp-${Date.now()}-${w}-${wd}-${Math.random().toString(36).slice(2)}`
          const row = {
            id, user_id: userId,
            ...toDbBlocco({
              type, label,
              startAt: s.toISOString(), endAt: e.toISOString(),
              recurringGroupId: groupId,
              recurrence: 'weekly',
            }),
          }
          rows.push(row)
          optimisticItems.push(mapBlocco(row))
        }
      }
    }

    // Optimistic insert nello store
    set(s => ({ blocchi: [...(s.blocchi ?? []), ...optimisticItems].sort(byStartAt) }))

    try {
      await dbFetch('blocchi', 'POST', rows)
    } catch (err) {
      // Rollback
      const idsInserted = new Set(rows.map(r => r.id))
      set(s => ({ blocchi: (s.blocchi ?? []).filter(b => !idsInserted.has(b.id)) }))
      throw err
    }
    return { groupId, count: rows.length }
  },

  updateBlocco: async (id, data) => {
    const prev = get().blocchi?.find(b => b.id === id)
    if (!prev) return
    // Optimistic
    set(s => ({
      blocchi: (s.blocchi ?? []).map(b => b.id === id ? { ...b, ...data } : b),
    }))
    try {
      await dbFetch(`blocchi?id=eq.${id}`, 'PATCH', toDbBlocco({ ...prev, ...data }))
    } catch (err) {
      // Rollback
      set(s => ({ blocchi: (s.blocchi ?? []).map(b => b.id === id ? prev : b) }))
      throw err
    }
  },

  deleteBlocco: async (id) => {
    await dbFetch(`blocchi?id=eq.${id}`, 'DELETE')
    set(s => ({ blocchi: (s.blocchi ?? []).filter(b => b.id !== id) }))
  },

  // Elimina tutte le occorrenze di una serie ricorrente
  deleteBloccoSeries: async (groupId) => {
    if (!groupId) return
    await dbFetch(`blocchi?recurring_group_id=eq.${groupId}`, 'DELETE')
    set(s => ({ blocchi: (s.blocchi ?? []).filter(b => b.recurringGroupId !== groupId) }))
  },
  }),
  {
    name: 'organizer-data',
    partialize: (state) => ({
      tasks:    state.tasks,
      scadenze: state.scadenze,
      blocchi:  state.blocchi,
    }),
  }
))

export default useTaskStore
